import { DataProvider } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import { getApiUrl } from './utils/apiUrl';

const apiUrl = getApiUrl();

// Create the data provider with authentication
const dataProvider = simpleRestProvider(apiUrl);

// Wrap the data provider to add authentication headers
const authenticatedDataProvider: DataProvider = {
    ...dataProvider,
    getList: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const url = new URL(`${apiUrl}/api/v1/admin/${resource}`, window.location.origin);
        
        // Add pagination parameters
        if (params.pagination) {
            url.searchParams.set('skip', ((params.pagination.page - 1) * params.pagination.perPage).toString());
            url.searchParams.set('limit', params.pagination.perPage.toString());
        }
        
        // Add sorting parameters
        if (params.sort) {
            url.searchParams.set('sort', params.sort.field);
            url.searchParams.set('order', params.sort.order.toLowerCase());
        }

        // Add search parameters
        if (params.filter) {
            Object.keys(params.filter).forEach(key => {
                const value = params.filter[key];
                if (value === null || value === undefined || value === '') return;
                if (Array.isArray(value)) {
                    if (value.length === 0) return;
                    value.forEach(v => url.searchParams.append(key, String(v)));
                } else {
                    url.searchParams.set(key, String(value));
                }
            });
        }

        const response = await fetch(url.toString(), {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Handle case where data is not in expected format
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response format from API');
        }

        // Ensure we have an array to map over
        const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];

        // Map uuid to id for React-Admin compatibility
        const mappedItems = items.map((item: any) => {
            // For all resources, map uuid to id for React-Admin compatibility
            return { ...item, id: item.uuid };
        });

        // For sales_reps, enrich with user emails
        if (resource === 'sales_reps') {
            console.log('Sales rep records:', mappedItems);
            const userIds = mappedItems
                .map((item: any) => item.user_uuid)
                .filter(Boolean);
            console.log('User IDs to fetch:', userIds);
            if (userIds.length > 0) {
                try {
                    const usersUrl = `${apiUrl}/api/v1/admin/users/many?ids=${userIds.join(',')}`;
                    console.log('Fetching users from:', usersUrl);
                    const usersResponse = await fetch(usersUrl, {
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                    });
                    console.log('Users response status:', usersResponse.status);
                    const usersResult = await usersResponse.json();
                    console.log('Users result:', usersResult);
                    if (usersResponse.ok) {
                        const usersArray = Array.isArray(usersResult.items) ? usersResult.items : Array.isArray(usersResult) ? usersResult : [];
                        const userMap = new Map(usersArray.map((u: any) => [u.uuid, u.email]));
                        console.log('User map:', Object.fromEntries(userMap));
                        mappedItems.forEach((item: any) => {
                            item.user_email = userMap.get(item.user_uuid) || '';
                        });
                    }
                } catch (e) {
                    console.error('Failed to fetch user emails:', e);
                }
            }
        }

        return {
            data: mappedItems,
            total: data.total || items.length,
        };
    },
    getOne: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${apiUrl}/api/v1/admin/${resource}/${params.id}`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('getOne result:', result);
        
        if (!result.data) {
            throw new Error('Record not found');
        }
        
        // Map uuid to id for React-Admin compatibility
        if (result.data.uuid === undefined) {
            throw new Error('Record not found');
        }
        const data = { ...result.data, id: result.data.uuid };
        return { data };
    },
    getMany: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${apiUrl}/api/v1/admin/${resource}/many?ids=${params.ids.join(',')}`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        // Handle case where result is not in expected format
        if (!result || typeof result !== 'object') {
            throw new Error('Invalid response format from API');
        }
        
        // Ensure we have an array to map over
        const items = Array.isArray(result.items) ? result.items : Array.isArray(result) ? result : [];
        
        // Map uuid to id for React-Admin compatibility
        const data = items.map((item: any) => {
            // For all resources, map uuid to id for React-Admin compatibility
            return { ...item, id: item.uuid };
        });
        return { data };
    },
    getManyReference: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${apiUrl}/api/v1/admin/${resource}?${params.target}=${params.id}`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Handle case where data is not in expected format
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid response format from API');
        }
        
        // Ensure we have an array to map over
        const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
        
        // Map uuid to id for React-Admin compatibility
        const mappedItems = items.map((item: any) => {
            // For all resources, map uuid to id for React-Admin compatibility
            return { ...item, id: item.uuid };
        });
        return {
            data: mappedItems,
            total: data.total || items.length,
        };
    },
    create: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${apiUrl}/api/v1/admin/${resource}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(params.data),
        });
        const result = await response.json();
        // Map uuid to id for React-Admin compatibility
        const data = { ...result.data, id: result.data.uuid };
        return { data };
    },
    update: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${apiUrl}/api/v1/admin/${resource}/${params.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(params.data),
        });
        const result = await response.json();
        // Map uuid to id for React-Admin compatibility
        const data = { ...result.data, id: result.data.uuid };
        return { data };
    },
    updateMany: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        await Promise.all(
            params.ids.map(id =>
                fetch(`${apiUrl}/api/v1/admin/${resource}/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(params.data),
                })
            )
        );
        return { data: params.ids };
    },
    delete: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        if (resource === 'knocks' || resource === 'users') {
            await fetch(`${apiUrl}/api/v1/admin/${resource}/${params.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ deleted_at: new Date().toISOString() }),
            });
        } else {
            await fetch(`${apiUrl}/api/v1/admin/${resource}/${params.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
        }
        return { data: params.previousData as any };
    },
    deleteMany: async (resource, params) => {
        const token = localStorage.getItem('admin_token');
        if (resource === 'knocks' || resource === 'users') {
            await Promise.all(
                params.ids.map(id =>
                    fetch(`${apiUrl}/api/v1/admin/${resource}/${id}`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({ deleted_at: new Date().toISOString() }),
                    })
                )
            );
        } else {
            await Promise.all(
                params.ids.map(id =>
                    fetch(`${apiUrl}/api/v1/admin/${resource}/${id}`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                    })
                )
            );
        }
        return { data: params.ids };
    },
};

export default authenticatedDataProvider; 
